import { DatePipe } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { SummaryCardAction, SummaryCardHorizontalItem } from '@next/core-lib/components';
import { LocaleService } from '@next/core-lib/i18n';
import { DataSeries } from '@next/core-lib/models';
import { Store } from '@ngrx/store';

import { AppState } from '../../../../shared/models';
import {
  Flock,
  FlockWeekData,
  WeekDataValue,
  WeekRegistrationKPIItem,
} from '../../../models/flock.model';
import { loadBreederFlockChart } from '../../../store/actions/flock.actions';
import { FlockOverviewWeekRegistrationCardComponent } from '../../shared/flock-overview-week-registration-card/flock-overview-week-registration-card.component';

const CARD_PARAMETERS = [
  'HenCount',
  'RoosterCount',
  'RoosterPercentage',
  'MortalityRate',
  'HenMortalityRate',
  'RoosterMortalityRate',
  'EggCount',
  'HatchingEggCount',
  'HatchedEggsPerPlacedHen',
  'ProductionFigure',
  'TotalFeedPerWeek',
] as const;

@Component({
  selector: 'app-breeder-flock-overview-week-registration-item',
  templateUrl: './breeder-flock-overview-week-registration-item.component.html',
  imports: [FlockOverviewWeekRegistrationCardComponent],
})
export class BreederFlockOverviewWeekRegistrationItemComponent {
  readonly flock = input.required<Flock>();
  readonly flockWeekData = input.required<FlockWeekData>();
  readonly flockWeekDataSeries = input<DataSeries<number, number>>();
  readonly editClicked = output<number>();
  readonly deleteClicked = output<number>();

  private readonly localeService = inject(LocaleService);
  private readonly locale = toSignal(this.localeService.locale$, {
    initialValue: this.localeService.getLocale(),
  });
  private readonly datePipe = inject(DatePipe);
  private readonly store = inject<Store<AppState>>(Store);
  readonly cardActions = computed<SummaryCardAction[]>(() => {
    this.locale();
    const weekData = this.flockWeekData();
    const flock = this.flock();
    return [
      {
        id: 'edit-action',
        icon: 'edit',
        label: this.localeService.translate('flock-overview.week-registrations.edit'),
        callback: () => this.editClicked.emit(weekData.week),
      },
      {
        id: 'delete-action',
        icon: 'delete',
        label: this.localeService.translate('flock-overview.week-registrations.delete'),
        callback: () => this.deleteClicked.emit(weekData.week),
        disabled:
          weekData.week !== flock.weekRegistrations.reduce((wr, u) => Math.max(wr, u.weekNr), 0),
      },
    ];
  });

  readonly cardItems = computed<SummaryCardHorizontalItem[]>(() => {
    this.locale();
    return CARD_PARAMETERS.flatMap((parameter) => {
      const item = this.flockWeekData().values?.find((value) => value.parameter === parameter);
      return item ? [this.mapCardItem(item)] : [];
    });
  });

  readonly title = computed(() => {
    this.locale();
    const weekData = this.flockWeekData();
    return `<div>${this.localeService.translate('week-registration.week')} ${weekData.week}</div>
    <div class="flex__child text--small">(${this.datePipe.transform(
      weekData.weekStartDate,
    )} - ${this.datePipe.transform(weekData.weekEndDate)})</div>`;
  });

  private mapCardItem(item: WeekDataValue): SummaryCardHorizontalItem {
    const cardItem: SummaryCardHorizontalItem = {
      label: this.localeService.translate(
        `flock-overview.week-registrations.breeder-kpis.${item.parameter}`,
      ),
      value: item.value ?? undefined,
      unit: this.getUnit(item),
      decimalPrecision: this.getDecimalPrecision(item),
    };
    const parameter = this.flockWeekDataSeries()?.metaData?.parameters?.find(
      ({ code }) => code === item.parameter,
    );

    if (parameter && Number.isFinite(item.value)) {
      cardItem.decimalPrecision = parameter.numberOfDecimals;
      cardItem.valueAction = () =>
        this.openChart({
          name: item.parameter,
          translationKey: `flock-overview.week-registrations.breeder-kpis.${item.parameter}`,
          unit: parameter.unit ?? item.unit,
          decimalsPrecision: parameter.numberOfDecimals,
        });
    }

    return cardItem;
  }

  private openChart(kpi: WeekRegistrationKPIItem): void {
    this.store.dispatch(loadBreederFlockChart({ flock: this.flock(), kpi }));
  }

  private getDecimalPrecision(item: WeekDataValue): number {
    return Number.isInteger(item.value) || item.value === null ? 0 : 2;
  }

  private getUnit(item: WeekDataValue): string | undefined {
    return item.unit;
  }
}
